import {
    ChangeDetectionStrategy,
    Component,
    ViewEncapsulation,
    input,
    effect,
    model,
    signal,
    computed,
    output,
    inject,
    OnInit,
} from '@angular/core';
import { IQuizEditItem, IQuizQuestion, IQuizQuestionAnswer } from '@fi-app/fi-onboarding/fi-quiz/fi-quiz';
import { IOnboardingCompletionUserStep, IOnboardingQuizCompletionTimerData } from '../../../fi-onboarding-completion';
import { FiOnboardingQuizCompletionStateType } from '../../../fi-onboarding-completion-enums';
import { isDefined } from '@fi-app/shared/utils/is-defined';
import { isNullOrUndefined } from '@fi-app/shared/utils/is-null-or-undefined';
import { FiOnboardingCompletionHelperService } from '../../../fi-onboarding-completion-helper.service';
import { FiOnboardingStepCompletionDialogState } from '../../../fi-onboarding-step-completion-dialog/fi-onboarding-step-completion-dialog-state.service';
@Component({
    selector: 'fi-quiz-in-progress',
    templateUrl: './quiz-in-progress.component.html',
    styleUrl: './quiz-in-progress.component.scss',
    changeDetection: ChangeDetectionStrategy.OnPush,
    encapsulation: ViewEncapsulation.None,
    host: { class: 'fi-quiz-in-progress' },
    standalone: false,
})
export class FiQuizInProgressComponent implements OnInit {
    public quiz = input.required<IQuizEditItem>();
    public isResultMode = input<boolean>(false);
    public correctAnsweredQuestions = input<IQuizQuestionAnswer[]>();
    public isReportModeQuizNotCompletedOrInProgress = input<boolean>(false);
    public userCompletionStep = model.required<IOnboardingCompletionUserStep>();
    public enableSubmit = output<void>();
    public timeIsOut = output<void>();

    private readonly fiOnboardingCompletionHelperService = inject(FiOnboardingCompletionHelperService);
    protected readonly completionDialogState = inject(FiOnboardingStepCompletionDialogState);

    protected readonly currentQuestionIndex = signal(0);
    protected readonly isCurrentQuestionCompleted = computed(
        () =>
            this.userCompletionStep().QuizCompletion?.Answers?.find(
                (answer) => answer.QuizQuestionId === this.quiz().Questions[this.currentQuestionIndex()].Id,
            )?.Value !== undefined,
    );

    protected readonly currentQuestion = computed<IQuizQuestion | undefined>(
        () => this.quiz().Questions[this.currentQuestionIndex()],
    );
    protected readonly currentQuestionAnswer = computed<IQuizQuestionAnswer>(() =>
        this.userCompletionStep().QuizCompletion?.Answers?.find(
            (answer) => answer.QuizQuestionId === this.currentQuestion()?.Id,
        ),
    );

    protected isQuizFailed = computed(() => {
        return this.userCompletionStep().QuizCompletion?.CompletionState === FiOnboardingQuizCompletionStateType.Failed;
    });

    protected readonly isQuizCompleted = computed(() => {
        return (
            this.userCompletionStep().QuizCompletion?.CompletionState === FiOnboardingQuizCompletionStateType.Completed
        );
    });

    protected readonly isRetakeAllowed = computed(() => this.quiz().AllowRetakes);

    protected readonly isCorrectAnswerRevealed = computed(() => {
        if (this.completionDialogState.isReportMode()) {
            return true;
        }
        return !this.isRetakeAllowed() || (this.isRetakeAllowed() && this.isQuizCompleted());
    });

    private readonly enableSubmitEffect = effect(() => {
        if (this.currentQuestionIndex() === this.quiz().Questions.length - 1 && !this.isResultMode()) {
            this.enableSubmit.emit();
            this.enableSubmitEffect.destroy();
        }
    });

    public ngOnInit(): void {
        if (!this.isResultMode() && !this.completionDialogState.isReportMode()) {
            this.currentQuestionIndex.set(
                this.userCompletionStep().QuizCompletion?.Answers.findIndex((answer) => !answer.IsDisabled),
            ) ?? 0;
        }
    }

    protected getAnswerByQuestionId(questionId: number) {
        return this.userCompletionStep().QuizCompletion?.Answers?.find(
            (answer) => answer.QuizQuestionId === questionId,
        );
    }

    protected readonly isNextQuestionDisabled = computed(
        () => this.currentQuestionIndex() >= this.quiz().Questions.length - 1,
    );

    protected readonly isPreviousQuestionDisabled = computed(() => this.currentQuestionIndex() <= 0);

    protected readonly isQuizTimerEnabled = computed<boolean>(() => this.quiz().IsTimerEnabled && !this.isResultMode());

    protected readonly quizTimerData = computed<IOnboardingQuizCompletionTimerData | undefined>(() => {
        if (!this.isQuizTimerEnabled()) {
            return undefined;
        }
        const startDateTime = this.userCompletionStep()?.QuizCompletion?.StartDateTime;
        const timerMs = this.quiz()?.TimerMs;
        if (isNullOrUndefined(startDateTime) || isNullOrUndefined(timerMs)) {
            return undefined;
        }
        return this.fiOnboardingCompletionHelperService.getQuizTimerData(timerMs, startDateTime);
    });

    protected onTimeIsOut() {
        this.timeIsOut.emit();
    }

    protected setCurrentQuestionIndex(index: number): void {
        this.currentQuestionIndex.set(index);
    }

    protected onNextQuestion(): void {
        this.currentQuestionIndex.update((index) => index + 1);
    }

    protected onPreviousQuestion(): void {
        this.currentQuestionIndex.update((index) => index - 1);
    }

    protected answeredQuestions = computed<IQuizQuestionAnswer[]>(() => {
        return this.userCompletionStep().QuizCompletion?.Answers?.filter((answer) => isDefined(answer.Value)) ?? [];
    });

    protected onAnswerChange(questionId: number, answer: IQuizQuestionAnswer): void {
        this.userCompletionStep.update((userCompletionStep) => {
            const existingAnswers = userCompletionStep.QuizCompletion?.Answers ?? [];
            const answerIndex = existingAnswers.findIndex((a) => a.QuizQuestionId === questionId);

            const updatedAnswers =
                answerIndex !== -1
                    ? existingAnswers.map((a, index) => (index === answerIndex ? answer : a))
                    : [...existingAnswers, answer];

            const existingQuizCompletion = userCompletionStep.QuizCompletion;
            const newUserCompletionStep: IOnboardingCompletionUserStep = {
                ...userCompletionStep,
                QuizCompletion: {
                    ...existingQuizCompletion,
                    CompletionState:
                        existingQuizCompletion?.CompletionState ?? FiOnboardingQuizCompletionStateType.InProgress,
                    Answers: updatedAnswers,
                },
            };

            return newUserCompletionStep;
        });
    }
}
